/*
 * Copyright 2020 Thomer Gil [https://thomer.com/games/brickbreaker-js/]
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import * as ex from 'excalibur';

const BB = {
    // colors
    BGCOLOR: ex.Color.Black,
    FGCOLOR: ex.Color.White,

    // wall properties
    NBRICKS: 13,
    NROWS: 8,
    ABOVE_WALL_EMPTY_SPACE: 60,
    BELOW_WALL_EMPTY_SPACE: 260,
    BRICK_WIDTH: 56,
    BRICK_HEIGHT: 15,
    BRICK_HSPACE: 2, // pixels; ideally an even number; it's divided by 2 later
    BRICK_VSPACE: 2,
    BRICK_COLORS: [
        // https://www.oreilly.com/library/view/raspberry-pi-for/9781118554234/a5_20_9781118554234-ch13.html
        // https://www.rapidtables.com/web/color/RGB_Color.html
        ex.Color.fromRGB(255, 100, 100), // red
        ex.Color.fromRGB(0, 255, 0), // lime
        ex.Color.fromRGB(128, 0, 128), // purple
        ex.Color.fromRGB(255, 255, 0), // yellow
        ex.Color.fromRGB(0, 0, 255), // blue
        ex.Color.fromRGB(0, 128, 0), // green
        ex.Color.fromRGB(255, 165, 0), // orange
        ex.Color.fromRGB(128, 128, 128), // gray
        ex.Color.fromRGB(0, 255, 255), // cyan/aqua
        ex.Color.fromRGB(0, 128, 128), // teal
        ex.Color.fromRGB(128, 128, 0), // olive
        ex.Color.fromRGB(138, 43, 226), // blue violet
        ex.Color.fromRGB(245, 222, 179), // wheat
        ex.Color.fromRGB(230, 230, 250), // lavender
        ex.Color.fromRGB(0, 0, 128), // navy
        ex.Color.fromRGB(127, 255, 212), // aqua marine
        ex.Color.fromRGB(169, 169, 169), // dark grey
        ex.Color.fromRGB(105, 105, 105), // dim grey
        ex.Color.fromRGB(0, 0, 0), // black
        ex.Color.fromRGB(0, 0, 0), // black
        ex.Color.fromRGB(0, 0, 0), // black
    ],

    // ball properties
    NBALLS: 1,
    BALL_COLOR: ex.Color.White,
    BALL_RADIUS: 4,
    BELOW_WALL_SPACE_TO_BALL: 60, // where ball is released
    BALL_Y_SPEED: 3,
    BALL_X_SPEED_SLOW: 2,
    BALL_X_SPEED_FAST: 4,
    BALL_RELEASE_X_JITTER: 3, // % of screen width

    // lives and levels
    NLIVES: 3,
    LEVEL_START: 1, // start at level

    // where to draw levels
    LEVEL_X_FROM_RIGHT: 15,
    LEVEL_Y: 20,
    LEVEL_SIZE: 12,

    // where to draw the lives
    LIVES_X: 10,
    LIVES_Y: 10,
    LIVES_BALL_RADIUS: 4,
    LIVES_SPACE: 12,
    LIVES_INFINITE: false,

    // level up and death dynamics
    COUNTDOWN_SECONDS: 3, // countdown before ball releases (3, 2, 1, GO!)
    DEATH_SLEEP: 0.1,     // minimal pause after ball lost
    NLIVES_LEVEL_UP: 1,
    LEVEL_UP_MULT: 1.15,

    // paddle properties
    PADDLE_HEIGHT: 12,
    PADDLE_WIDTH: 80,
    PADDLE_SLOW_MOVE: 2,
    PADDLE_MOVE: 5,
    MAX_PADDLE_MOVE: 10,
    PADDLE_COLOR: ex.Color.White,
    BELOW_PADDLE_EMPTY_SPACE: 0,
    PADDLE_AUTO: false,

    TIMESCALE: 60,

    // paddle bounce zones; set in startBrickBreaker
    PADDLE_ZONES: {},

    // global variables
    screenWidth: 0,
    screenHeight: 0,
};

export { BB };
